import React from 'react';
import { Target, MapPin, Map, Plus } from 'react-feather';
import { Zone, RadiusConfig, PostcodeConfig } from '../types';
import './ZonesView.css';

interface ZonesViewProps {
  zones: Zone[];
  onToggleZone: (zoneId: string) => void;
  onDeleteZone: (zoneId: string) => void;
  onAddZone: () => void;
  onBack: () => void;
}

export const ZonesView: React.FC<ZonesViewProps> = ({ 
  zones, 
  onToggleZone, 
  onDeleteZone, 
  onAddZone, 
  onBack 
}) => {
  const formatZoneDescription = (zone: Zone) => {
    if (zone.type === 'radius') {
      const config = zone.config as RadiusConfig;
      // Truncate long addresses
      const shortAddress = config.address.length > 30 
        ? config.address.substring(0, 30) + '...' 
        : config.address;
      return `${config.radiusKm}km radius around ${shortAddress}`;
    } else if (zone.type === 'postcodes') {
      const config = zone.config as PostcodeConfig;
      const postcodeCount = config.postcodes?.length || 0;
      return `${config.state} - ${postcodeCount} postcode${postcodeCount !== 1 ? 's' : ''}`;
    } else if (zone.type === 'polygon') {
      // Calculate number of points for polygon
      const config = zone.config as any;
      const pointCount = config.points?.length || 0;
      return `Custom area with ${pointCount} points`;
    } else {
      return 'Custom area';
    }
  };

  return (
    <div className="page zones-page">
      <div className="zones-header">
        <h1>Alert Zones</h1>
        <p>Manage areas you want to monitor for incidents</p>
      </div>

      <div className="zones-content">
        {zones.length === 0 ? (
          <div className="empty-zones">
            <div className="empty-icon">
              <Target size={32} />
            </div>
            <h3>No zones configured</h3>
            <p>Add your first zone to start receiving targeted alerts</p>
          </div>
        ) : (
          <div className="zones-list">
            {zones.map((zone) => (
              <div key={zone.id} className={`zone-card ${zone.enabled ? 'enabled' : 'disabled'}`}>
                <div className="zone-header">
                  <div className="zone-info">
                    <div className="zone-type-icon">
                      {zone.type === 'radius' ? <MapPin size={20} /> : <Map size={20} />}
                    </div>
                    <div className="zone-details">
                      <h3 className="zone-name">{zone.name}</h3>
                      <p className="zone-description">
                        {formatZoneDescription(zone)}
                      </p>
                    </div>
                  </div>
                  <div className="zone-actions">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={zone.enabled}
                        onChange={() => onToggleZone(zone.id)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
                <div className="zone-footer">
                  <span className="zone-date">
                    Added {zone.createdAt.toLocaleDateString()}
                  </span>
                  <button 
                    className="delete-btn"
                    onClick={() => onDeleteZone(zone.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="add-zone-section">
          <button className="btn btn-primary add-zone-btn" onClick={onAddZone}>
            <Plus size={16} className="add-icon" />
            Add New Zone
          </button>
        </div>
      </div>
    </div>
  );
}; 